/**
 * base-service — Axios; header X-PhanHe từ phanHeOverride || sessionStorage || 'bsc'
 * (không đọc authStore.phanHe — đã bỏ persist).
 */
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import CryptoJS from 'crypto-js';
import ENVIRONMENT from '../../environment/environment';
import { isVanHoaCultureApiPath, resolveCultureApiOrigin } from '@/configs/cultureApiOrigin';
import {
    isActionPlanV2ScreenActive,
    resolveActionPlanApiBaseURL,
    resolveActionPlanV2ApiOrigin,
    type ActionPlanApiTarget,
} from '@/configs/actionPlanV2ApiOrigin';
import { isQuyTrinhApiPath, resolveQtApiOrigin } from '@/configs/qtApiOrigin';
import { aiHubGatewayHeaders, isAiHubApiPath, resolveAiHubApiHost } from '@/configs/aiHubApiOrigin';
import { isOrganizationApiPath, resolveOrgApiOrigin } from '@/configs/organizationApiOrigin';
import { useAuthStore } from '@/store/useAuthStore';
import { getPhanHeFromStorage } from '@/utils/phanHeStorage';
import { formatErrorAlertMessage, getErrorMessage } from '../hooks/useErrorHandle';
import { tokenService } from './token/token-service';
import { SweetAlertService } from './alert/sweetAlertService';
import type { ApiResponse } from '@/types/base';
import { getTaskCompletionError } from './quy-trinh/task-completion-error';
import { ROUTES } from '../utils/pathRoutes';
import requestManager from './request-manager';
import { usePrivateStore } from '@/store/usePrivateStore';
import { IDLE_FEATURE_ENABLED } from '@/constants/idle';
import { isAppIdleActive } from '@/utils/idleSession';

declare module 'axios' {
    export interface AxiosRequestConfig {
        authorization?: boolean;
        /** Bearer token tạm cho request này; không ghi vào auth store */
        accessTokenOverride?: string;
        /** Tenant ID tạm cho header X-Tenant-ID */
        tenantIdOverride?: string;
        /** Phân hệ tạm cho header X-PhanHe */
        phanHeOverride?: string;
        /** Không hiện SweetAlert khi 404 (vd. repair-steps optional) */
        silentNotFound?: boolean;
        /** Không hiện SweetAlert khi lỗi (vd. column-configs list/values nền) */
        silentError?: boolean;
        /** Trả lỗi cho caller thay vì đổi thành kết quả rỗng. */
        rejectOnError?: boolean;
        /** Link khảo sát khách (stoken): không retry 401 bằng JWT */
        guestSurvey?: boolean;
        /** Gọi MS.ActionPlan (Action Plan v2) thay vì API monolith */
        actionPlanV2Api?: boolean;
        /** Chọn đích Action Plan tường minh; bỏ trống để giữ routing hiện tại */
        actionPlanApiTarget?: ActionPlanApiTarget;
    }
    export interface InternalAxiosRequestConfig {
        authorization?: boolean;
        timeout?: number;
        accessTokenOverride?: string;
        tenantIdOverride?: string;
        phanHeOverride?: string;
        silentNotFound?: boolean;
        silentError?: boolean;
        rejectOnError?: boolean;
        guestSurvey?: boolean;
        actionPlanV2Api?: boolean;
        actionPlanApiTarget?: ActionPlanApiTarget;
    }
}

type EnvironmentWithChecksum = typeof ENVIRONMENT & {
    CHECKSUM_SECRET?: string;
};

const request = axios.create({
    baseURL: process.env.NODE_ENV === 'production' ? ENVIRONMENT.API_HOST : String(process.env.CONFIG_NEXT_PUBLIC_API_BASE_URL || ENVIRONMENT.API_HOST_DEV),
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,
    timeout: 15 * 60 * 1000,
});

const getQtProxyDiagnostic = (config: InternalAxiosRequestConfig | undefined) => {
    if (process.env.NODE_ENV === 'production' || !config || !isQuyTrinhApiPath(config.url)) {
        return null;
    }

    const requestUri = request.getUri(config);
    const browserRequestUrl =
        typeof window !== 'undefined'
            ? new URL(requestUri, window.location.origin).toString()
            : requestUri;
    const expectedForwardUrl =
        typeof window !== 'undefined'
        && new URL(browserRequestUrl).origin === window.location.origin
            ? new URL(
                `${new URL(browserRequestUrl).pathname}${new URL(browserRequestUrl).search}`,
                ENVIRONMENT.BASE_URL,
            ).toString()
            : browserRequestUrl;

    return { browserRequestUrl, expectedForwardUrl };
};

/**
 * `next.config.ts` có `trailingSlash: true` → Next trả 308 từ `/api/v1/.../segment` sang `.../segment/`.
 * Sau redirect, một số client không gửi lại `Authorization` → MS.Culture thấy không JWT → 401.
 * Chuẩn hóa path (khớp `trailingSlash: true` trong Next) để tránh 308 — sau redirect một số client không gửi lại Authorization.
 */
const normalizeApiPathTrailingSlash = (url: string | undefined): string | undefined => {
    if (!url || url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    const q = url.indexOf('?');
    const path = q === -1 ? url : url.slice(0, q);
    const query = q === -1 ? '' : url.slice(q);
    if (!path || path.endsWith('/') || isVanHoaCultureApiPath(path)) {
        return url;
    }
    const lastSeg = path.split('/').filter(Boolean).pop() ?? '';
    if (lastSeg.includes('.')) {
        return url;
    }
    if (!/^\/v1\/(VH|vh|HanhViThenChot|quytrinh|masterprocess|vanhoa|license|notification|locale)/.test(path)) {
        return url;
    }
    return `${path}/${query}`;
};

// Secret tạm thời ở FE (NEXT_PUBLIC / ENVIRONMENT) — phải trùng BE Checksum:Secret.
// Không chống giả mạo thật vì secret lộ trong bundle; chỉ integrity nhẹ.
const CHECKSUM_SECRET =
    (ENVIRONMENT as EnvironmentWithChecksum).CHECKSUM_SECRET ||
    process.env.NEXT_PUBLIC_CHECKSUM_SECRET ||
    'Toppion@2026#DefaultChecksumKey!9xP7';

/** Chuẩn hóa payload checksum: chữ thường, bỏ khoảng trắng và dấu " ' (khớp BE). */
const normalizeChecksumPayload = (value: string | null | undefined): string => {
    if (value == null) return '';
    return String(value).toLowerCase().replace(/[\s"']/gu, '');
};

const isBlankChecksumText = (value: string | null | undefined): boolean => {
    if (value == null) return true;
    const trimmed = String(value).trim();
    if (!trimmed) return true;
    const lower = trimmed.toLowerCase();
    return lower === 'null' || lower === 'undefined';
};

const isFormLikeBody = (config: InternalAxiosRequestConfig): boolean => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
        return true;
    }
    if (typeof URLSearchParams !== 'undefined' && config.data instanceof URLSearchParams) {
        return true;
    }
    const contentType = String(
        config.headers?.['Content-Type'] ??
            config.headers?.['content-type'] ??
            '',
    ).toLowerCase();
    return contentType.includes('multipart/form-data')
        || contentType.includes('application/x-www-form-urlencoded');
};

/** Body đưa vào checksum: null / rỗng / chỉ khoảng trắng / "null" → chuỗi rỗng. */
const serializeChecksumBody = (config: InternalAxiosRequestConfig): string => {
    if (isFormLikeBody(config)) {
        return '[form-data]';
    }

    const data = config.data;
    if (data === undefined || data === null) {
        return '';
    }
    if (typeof data === 'string') {
        return isBlankChecksumText(data) ? '' : data.trim();
    }

    try {
        const serialized = JSON.stringify(data);
        return isBlankChecksumText(serialized) ? '' : serialized;
    } catch {
        return '[unserializable-body]';
    }
};

// Hàm helper để tạo checksum cho request
const generateChecksum = async (config: InternalAxiosRequestConfig): Promise<string> => {
    try {
        const encoder = new TextEncoder();

        const method = (config.method || '').trim().toUpperCase();
        const secret = String(CHECKSUM_SECRET ?? '').trim();
        const body = serializeChecksumBody(config);

        const payload = normalizeChecksumPayload(
            `${method}|${body}|${secret}`
        );

        const data = encoder.encode(payload);

        // Web Crypto chỉ có SubtleCrypto trong secure context; crypto-js giữ checksum chạy được ở HTTP nội bộ.
        const subtle = globalThis.crypto?.subtle;
        if (subtle) {
            const hashBuffer = await subtle.digest('SHA-256', data);
            const hashArray = Array.from(new Uint8Array(hashBuffer));
            return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
        }

        return CryptoJS.SHA256(payload).toString(CryptoJS.enc.Hex);
    } catch (err) {
        console.error('❌ Error generating checksum:', err);
        return '';
    }
};

// Track retry attempts to prevent infinite loops
const retryQueue = new Set<string>();

// sessionStorage key để phát hiện vòng lặp 401 redirect qua page reload
const REDIRECT_LOOP_GUARD_KEY = 'api-401-redirect-guard';
const REDIRECT_LOOP_WINDOW_MS = 15_000; // 15 giây — đủ để cover redirect login → quay lại

const isIn401RedirectLoop = (): boolean => {
    try {
        const raw = sessionStorage.getItem(REDIRECT_LOOP_GUARD_KEY);
        if (!raw) return false;
        const ts = parseInt(raw, 10);
        if (Date.now() - ts < REDIRECT_LOOP_WINDOW_MS) return true;
        sessionStorage.removeItem(REDIRECT_LOOP_GUARD_KEY);
        return false;
    } catch {
        return false;
    }
};

const mark401RedirectGuard = (): void => {
    try { sessionStorage.setItem(REDIRECT_LOOP_GUARD_KEY, Date.now().toString()); } catch { /* ignore */ }
};

const clear401RedirectGuard = (): void => {
    try { sessionStorage.removeItem(REDIRECT_LOOP_GUARD_KEY); } catch { /* ignore */ }
};

const stopAuthorizedRequest = (
    config: InternalAxiosRequestConfig,
    message = 'Phiên đăng nhập đã hết hạn.'
): InternalAxiosRequestConfig => {
    config.adapter = async () => ({
        data: {
            success: false,
            is401: true,
            message,
            data: null,
        },
        status: 200,
        statusText: 'OK',
        headers: {},
        config,
        request: {},
    });

    return config;
};

const unauthorizedResult = (message = 'Phiên đăng nhập đã hết hạn.') => Promise.resolve({
    success: false,
    is401: true,
    message,
    data: null,
});

request.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
        if (IDLE_FEATURE_ENABLED && isAppIdleActive() && config.authorization) {
            return stopAuthorizedRequest(
                config,
                'Ứng dụng đang ở chế độ nghỉ. Vui lòng nhấn "Tiếp tục làm việc".',
            );
        }

        // Quản lý request với RequestManager
        requestManager.addRequest(config);

        config.url = normalizeApiPathTrailingSlash(config.url);

        const actionPlanBaseURL = resolveActionPlanApiBaseURL({
            actionPlanApiTarget: config.actionPlanApiTarget,
            actionPlanV2Api: Boolean(config.actionPlanV2Api),
            actionPlanV2Origin: resolveActionPlanV2ApiOrigin(),
            actionPlanV2ScreenActive: isActionPlanV2ScreenActive(),
            isDevelopment: process.env.NODE_ENV !== 'production',
            url: config.url,
        });
        if (actionPlanBaseURL) {
            config.baseURL = actionPlanBaseURL;
        }

        // AI Agent Hub: engine Python có thể ở origin riêng (CONFIG_NEXT_PUBLIC_AIHUB_API_BASE_URL / env.js).
        const aiHubApiHost = resolveAiHubApiHost();
        if (aiHubApiHost && isAiHubApiPath(config.url)) {
            config.baseURL = aiHubApiHost;
        }
        // Engine dùng chung nhiều môi trường: báo gateway API môi trường hiện tại để engine kiểm quyền đúng máy chủ
        if (isAiHubApiPath(config.url)) {
            Object.entries(aiHubGatewayHeaders()).forEach(([k, v]) => config.headers.set(k, v));
        }

        // Dev: gọi thẳng microservice khi set NEXT_PUBLIC_* / CONFIG_* (tránh rebuild/proxy Next).
        const cultureOrigin = resolveCultureApiOrigin();
        if (isVanHoaCultureApiPath(config.url)) {
            config.baseURL = cultureOrigin ? `${cultureOrigin}/api` : ENVIRONMENT.CULTURE_API_HOST;
        } else if (process.env.NODE_ENV !== 'production') {
            const qtOrigin = resolveQtApiOrigin();
            if (qtOrigin && isQuyTrinhApiPath(config.url)) {
                config.baseURL = `${qtOrigin}/api`;
            } else {
                const orgOrigin = resolveOrgApiOrigin();
                if (orgOrigin && isOrganizationApiPath(config.url)) {
                    config.baseURL = `${orgOrigin}/api`;
                }
            }
        }

        // Gắn timestamp vào params để tránh browser disk cache
        config.params = {
            ...config.params,
            _t: Date.now(),
        };

        // Xử lý timeout: nếu không có timeout trong config, tự động detect export/backup
        const url = config.url?.toLowerCase() || '';
        const isExportOrBackup =
            url.includes('export') ||
            url.includes('backup') ||
            url.includes('perspective') ||
            url.includes('performance') ||
            url.includes('fishbone') ||
            url.includes('restore') ||
            url.includes('migrate') ||
            url.includes('tenant');

        if (isExportOrBackup) {
            // Set timeout 30 phút cho export và backup
            config.timeout = 180 * 60000; // 15 phút
        }

        // Nếu data là FormData, xóa Content-Type để browser tự set với boundary
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        if (config.tenantIdOverride) {
            config.headers['X-Tenant-ID'] = config.tenantIdOverride;
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
        }

        if (config.authorization) {
            const isAuth = useAuthStore.getState().isAuth;
            const useOverrideToken = Boolean(config.accessTokenOverride);
            let token = config.accessTokenOverride ?? useAuthStore.getState().accessToken;
            const tenantID = config.tenantIdOverride ?? useAuthStore.getState().tenantID;
            const phanHe = config.phanHeOverride ?? getPhanHeFromStorage() ?? 'bsc';

            // Nếu không có token, kiểm tra xem có cần redirect không
            if (!token && !useOverrideToken) {
                // Chỉ redirect khi isAuth === false (đã xác nhận là không authenticated)
                // Không redirect nếu isAuth là null (có thể đang trong quá trình load)
                if (isAuth === false) {
                    const returnUrl = document.location.pathname;
                    const isOnLoginPage = returnUrl.toLowerCase().includes(ROUTES.LOGIN.toLowerCase());
                    const isOnLogoutPage = returnUrl.toLowerCase().includes(ROUTES.LOGOUT.toLowerCase());

                    // Chỉ redirect nếu không đang ở trang login hoặc logout
                    if (!isOnLoginPage && !isOnLogoutPage) {
                        if (returnUrl && returnUrl != '/' && returnUrl != '') {
                            document.location.href = `${ROUTES.LOGOUT}?return-url=${encodeURIComponent(returnUrl)}`;
                        } else {
                            document.location.href = `${ROUTES.LOGOUT}`;
                        }
                        // Return a local response to avoid sending an unauthenticated request.
                        return stopAuthorizedRequest(config);
                    }
                }
                // Còn thiếu token (vd. isAuth null/true nhưng persist chưa rehydrate xong, hoặc store lệch) —
                // không được gửi request có authorization: true mà không có Bearer (Culture sẽ 401).
                return stopAuthorizedRequest(
                    config,
                    'Phiên đăng nhập chưa sẵn sàng hoặc đã hết hạn.',
                );
            }

            // Refresh token chỉ với token đăng nhập hiện tại, không áp dụng token override
            if (token && !useOverrideToken) {
                if (tokenService.isTokenExpired(token)) {
                    console.warn('⚠️ Token expired, attempting to refresh...');

                    try {
                        const tokenResult = await tokenService.validateAndRefreshToken();

                        if (tokenResult.isValid && tokenResult.token) {
                            token = tokenResult.token;
                        } else {
                            console.error('❌ Token refresh failed, redirecting to logout');
                            await tokenService.handleTokenExpired();
                            return stopAuthorizedRequest(config);
                        }
                    } catch (error) {
                        console.error('Error refreshing token', {
                            message: error instanceof Error ? error.message : String(error),
                        });
                        await tokenService.handleTokenExpired();
                        return stopAuthorizedRequest(config);
                    }
                }
            }

            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            config.headers['X-Requested-With'] = `XMLHttpRequest`;
            config.headers['X-Reset-RateLimit'] = `reset`;
            config.headers['X-Tenant-ID'] = tenantID;
            config.headers['X-PhanHe'] = phanHe;
            config.headers['X-SignalR-Auth'] = 'true';
        }

        // Tạo checksum cho request và gắn vào header trước khi gửi lên server
        try {
            const checksum = await generateChecksum(config);
            if (checksum) {
                config.headers['X-Checksum'] = checksum;
            }
        } catch (err) {
            console.error('❌ Failed to attach checksum header:', err);
        }
        const qtProxyDiagnostic = getQtProxyDiagnostic(config);
        if (qtProxyDiagnostic) {
            console.info('QT proxy request', JSON.stringify(qtProxyDiagnostic));
        }
        return config;
    },
    (error) => Promise.reject(error)
);
// Response interceptor - Xử lý response và errors
request.interceptors.response.use(
    (response) => {
        // Response thành công, xóa request khỏi manager
        if (response.config) {
            requestManager.removeRequest(response.config);
        }
        const qtProxyDiagnostic = getQtProxyDiagnostic(response.config);
        if (qtProxyDiagnostic) {
            console.info('QT proxy response', JSON.stringify({
                ...qtProxyDiagnostic,
                status: response.status,
                upstreamCorrelationId: response.headers?.['x-correlation-id'] ?? null,
            }));
        }
        // Clear loop guard khi có bất kỳ request thành công nào
        clear401RedirectGuard();
        return response.data;
    },
    async (error: AxiosError) => {

        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (originalRequest?.rejectOnError) {
            requestManager.removeRequest(originalRequest);
            return Promise.reject(error);
        }

        // Request bị abort (logout / idle / cancelAll) — không hiện SweetAlert
        const isCanceled =
            axios.isCancel(error)
            || error.code === 'ERR_CANCELED'
            || error.name === 'CanceledError'
            || /cancel(led)?|aborted/i.test(String(error.message || ''));
        if (isCanceled) {
            if (originalRequest) {
                requestManager.removeRequest(originalRequest);
            }
            return Promise.resolve(null);
        }

        const isAuth = useAuthStore.getState().isAuth;
        const status = error.response?.status;
        const qtProxyDiagnostic = getQtProxyDiagnostic(originalRequest);
        const errorSummary = {
            status,
            url: originalRequest?.url,
            method: originalRequest?.method,
            message: error.message,
            ...(qtProxyDiagnostic
                ? {
                    ...qtProxyDiagnostic,
                    responseBody: error.response?.data,
                    upstreamCorrelationId: error.response?.headers?.['x-correlation-id'] ?? null,
                }
                : {}),
        };

        if (status === 401) {
            console.warn('API request unauthorized', errorSummary);
        } else if (qtProxyDiagnostic) {
            console.error('QT proxy error', JSON.stringify(errorSummary));
        } else {
            console.error('API request failed', errorSummary);
        }

        // Tạo unique key cho request để track retry
        const requestKey = `${originalRequest?.method}-${originalRequest?.url}`;

        // Nếu có response từ server
        if (error.response) {
            const { status, data } = error.response;

            console.log('❌ Error:', error.response,status, data);

            switch (status) {
                case 401: {
                    if (originalRequest?.guestSurvey) {
                        if (originalRequest) {
                            requestManager.removeRequest(originalRequest);
                        }
                        return Promise.reject(error);
                    }
                    if (isAuth == null || !isAuth) {
                        if (originalRequest) {
                            requestManager.removeRequest(originalRequest);
                        }
                        return unauthorizedResult();
                    }
                    // Không retry khi dùng token tạm (tenant migrate) — tránh ghi đè bằng token user
                    if (originalRequest?.accessTokenOverride) {
                        if (originalRequest) {
                            requestManager.removeRequest(originalRequest);
                        }
                        return Promise.reject(error);
                    }
                    // Unauthorized - Token hết hạn hoặc không hợp lệ
                    if (originalRequest && !originalRequest._retry && !retryQueue.has(requestKey)) {
                        originalRequest._retry = true;
                        retryQueue.add(requestKey);

                        try {

                            // Xóa store private
                            usePrivateStore.getState().clearStore();

                            // Sử dụng TokenService để validate và refresh token
                            const tokenResult = await tokenService.validateAndRefreshToken();

                            if (tokenResult.isValid && tokenResult.token) {

                                // Cập nhật Authorization header với token mới
                                originalRequest.headers['Authorization'] = `Bearer ${tokenResult.token}`;

                                // Remove từ retry queue sau khi thành công
                                retryQueue.delete(requestKey);

                                // Retry request gốc với token mới (request mới sẽ được addRequest tự động qua interceptor)
                                return request(originalRequest);
                            } else {
                                console.error('❌ Token refresh failed');
                                retryQueue.delete(requestKey);
                                // Xóa request khỏi manager khi không retry được
                                if (originalRequest) {
                                    requestManager.removeRequest(originalRequest);
                                }
                                if (!isIn401RedirectLoop()) {
                                    mark401RedirectGuard();
                                    await tokenService.handleTokenExpired();
                                }
                                return unauthorizedResult();
                            }
                        } catch (refreshError) {
                            console.error('Token refresh exception', {
                                message: refreshError instanceof Error ? refreshError.message : String(refreshError),
                            });
                            retryQueue.delete(requestKey);
                            // Xóa request khỏi manager khi lỗi
                            if (originalRequest) {
                                requestManager.removeRequest(originalRequest);
                            }
                            if (!isIn401RedirectLoop()) {
                                mark401RedirectGuard();
                                await tokenService.handleTokenExpired();
                            }
                            return unauthorizedResult();
                        }
                    } else {
                        console.warn('⚠️ 401 error - Already retried or in retry queue');
                        // Xóa request khỏi manager khi không retry được
                        if (originalRequest) {
                            requestManager.removeRequest(originalRequest);
                        }
                        if (!isIn401RedirectLoop()) {
                            mark401RedirectGuard();
                            await tokenService.handleTokenExpired();
                        }
                        return unauthorizedResult();
                    }
                    break;
                }

                case 403: {
                    const errorData = data as { message?: string } | undefined;
                    // Xóa request khỏi manager
                    if (originalRequest) {
                        requestManager.removeRequest(originalRequest);
                    }
                    return Promise.resolve({
                        success: false,
                        is403: true,
                        message: errorData?.message || 'Bạn không có quyền truy cập vào trang này',
                        data: null
                    });
                }

                case 404: {
                    const url = originalRequest?.url || '';
                    const isComparePager = url.includes('/compare-pager');
                    // Chỉ console.error cho /compare-pager, các URL khác vẫn hiện alert
                    if (isComparePager) {
                        console.error('⚠️ API endpoint not found:', originalRequest?.url, status, data);
                    } else {
                        console.warn('⚠️ API endpoint not found:', originalRequest?.url);
                        if (!originalRequest?.silentNotFound && !originalRequest?.silentError) {
                            // SweetAlertService.warningAsync('Không tìm thấy dữ liệu', 'Dữ liệu bạn yêu cầu không tồn tại hoặc đã bị xóa.');
                            SweetAlertService.showHandleErrorApiAsync({description: 'Dữ liệu bạn yêu cầu không tồn tại hoặc đã bị xóa.'})
                        }
                    }
                    // Xóa request khỏi manager
                    if (originalRequest) {
                        requestManager.removeRequest(originalRequest);
                    }
                    return Promise.resolve(null);
                }

                case 405: {
                    const url = originalRequest?.url || '';
                    const isComparePager = url.includes('/compare-pager');
                    // Chỉ console.error cho /compare-pager, các URL khác vẫn hiện alert
                    if (isComparePager) {
                        console.error('⚠️ API method not allowed:', originalRequest?.method, originalRequest?.url, status, data);
                    } else {
                        console.warn('⚠️ API method not allowed:', originalRequest?.method, originalRequest?.url);
                        if (!originalRequest?.silentError) {
                            // SweetAlertService.warningAsync('Phương thức không được phép', 'Phương thức HTTP không được hỗ trợ cho endpoint này.');
                            SweetAlertService.showHandleErrorApiAsync({description: 'Phương thức không được phép, Phương thức HTTP không được hỗ trợ cho endpoint này.'})
                        }
                    }
                    // Xóa request khỏi manager
                    if (originalRequest) {
                        requestManager.removeRequest(originalRequest);
                    }
                    return Promise.resolve(null);
                }

                case 400: {
                    if (originalRequest) {
                        requestManager.removeRequest(originalRequest);
                    }
                    const errorMessage = getErrorMessage(error, 'Dữ liệu không hợp lệ');
                    if (!originalRequest?.silentError && errorMessage.trim()) {
                        // SweetAlertService.warningAsync('', formatErrorAlertMessage(errorMessage));
                        const isTaskCompletion = /\/process-instance\/tasks\/complete\/?(?:\?|$)/.test(originalRequest?.url ?? '');
                        const errorCode = data && typeof data === 'object' && 'errorCode' in data
                            && typeof data.errorCode === 'string' ? data.errorCode : undefined;
                        SweetAlertService.showHandleErrorApiAsync(isTaskCompletion
                            ? getTaskCompletionError(errorMessage, errorCode)
                            : { title: 'Không thể thực hiện yêu cầu', description: errorMessage });
                    }
                    if (
                        data &&
                        typeof data === 'object' &&
                        ('success' in data || 'message' in data || 'errors' in data)
                    ) {
                        return Promise.resolve(data);
                    }
                    return Promise.resolve(null);
                }

                case 422: {
                    // Validation Error - Let the calling code handle validation errors
                    console.warn('⚠️ Validation error:', data);
                    // Xóa request khỏi manager
                    if (originalRequest) {
                        requestManager.removeRequest(originalRequest);
                    }
                    return Promise.reject(error);
                }

                case 429: {
                    // Too Many Requests
                    // Xóa request khỏi manager
                    if (originalRequest) {
                        requestManager.removeRequest(originalRequest);
                    }
                    if (!originalRequest?.silentError) {
                        // SweetAlertService.warningAsync('Quá nhiều yêu cầu', 'Vui lòng chờ một chút trước khi thử lại.');
                        SweetAlertService.showHandleErrorApiAsync({description: 'Quá nhiều yêu cầu, vui lòng chờ một chút trước khi thử lại.'})
                    }
                    // Không return error - đã xử lý bằng alert
                    return Promise.resolve(null);
                }

                case 500: {
                    // Server error - Lỗi máy chủ
                    const responseErrorData = data && typeof data === 'object'
                        ? data as { errors?: unknown[] }
                        : undefined;
                    const errorMessage =
                        responseErrorData && Array.isArray(responseErrorData.errors) && responseErrorData.errors.length > 0
                            ? String(responseErrorData.errors[0])
                            : 'Lỗi máy chủ';
                    // Xóa request khỏi manager
                    if (originalRequest) {
                        requestManager.removeRequest(originalRequest);
                    }
                    if (!originalRequest?.silentError) {
                        // SweetAlertService.errorAsync(errorMessage);
                        console.error('❌ Server error:', errorMessage);
                        SweetAlertService.showHandleErrorApiAsync({description: "Do lượng lượng truy cập tăng cao, rất mong bạn thông cảm. Chân thành xin lỗi!"})
                    }
                    // Không return error - đã xử lý bằng alert
                    return Promise.resolve(null);
                }
                case 502:
                case 503:
                case 504: {
                    // Server Errors
                    // Xóa request khỏi manager
                    if (originalRequest) {
                        requestManager.removeRequest(originalRequest);
                    }
                    if (!originalRequest?.silentError) {
                        // SweetAlertService.errorAsync('Lỗi máy chủ', 'Đã xảy ra lỗi máy chủ. Vui lòng thử lại sau.');
                        SweetAlertService.showHandleErrorApiAsync({description: "Do lượng lượng truy cập tăng cao, rất mong bạn thông cảm. Chân thành xin lỗi!"})
                    }
                    // Không return error - đã xử lý bằng alert
                    return Promise.resolve(null);
                }

                default: {
                    const errorMessage = getErrorMessage(error, 'Đã có lỗi xảy ra');
                    
                    // Xóa request khỏi manager
                    if (originalRequest) {
                        requestManager.removeRequest(originalRequest);
                    }
                    if (
                        data &&
                        typeof data === 'object' &&
                        ('success' in data || 'message' in data || 'errors' in data)
                    ) {
                        if (
                            !originalRequest?.silentError &&
                            (data as { success?: boolean }).success === false &&
                            errorMessage.trim()
                        ) {
                            // SweetAlertService.warningAsync('', formatErrorAlertMessage(errorMessage));
                            console.error('❌ Validation error:', formatErrorAlertMessage(errorMessage));
                        }
                        return Promise.resolve(data);
                    }
                    if (!originalRequest?.silentError && errorMessage.trim()) {
                        // SweetAlertService.warningAsync('', formatErrorAlertMessage(errorMessage));
                        console.error('❌ Validation error:', formatErrorAlertMessage(errorMessage));
                    }
                    return Promise.resolve(null);
                }
            }
        } else if (error.request) {
            // Request được gửi nhưng không nhận được response
            console.error('❌ No response from server:', error.request);
            // Xóa request khỏi manager
            if (originalRequest) {
                requestManager.removeRequest(originalRequest);
            }
            if (!originalRequest?.silentError) {
                // SweetAlertService.errorAsync(
                //     'Lỗi kết nối',
                //     'Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng.',
                //     () => {
                //         // Option để retry hoặc redirect
                //     }
                // );
                SweetAlertService.showHandleErrorApiAsync({description: 'Không thể kết nối đến máy chủ. Vui lòng kiểm tra kết nối mạng.'})
            }
            // Không return error - đã xử lý bằng alert
            return Promise.resolve(null);
        } else {
            // Lỗi khác (setup request, etc.)
            console.error('❌ Request setup error:', error.message);
            // Xóa request khỏi manager
            if (originalRequest) {
                requestManager.removeRequest(originalRequest);
            }
            if (!originalRequest?.silentError) {
                // SweetAlertService.errorAsync('Lỗi hệ thống', `Đã xảy ra lỗi: ${error.message}`, () => {
                // });
                SweetAlertService.showHandleErrorApiAsync({description: `Đã xảy ra lỗi: ${error.message}`})
            }
            // Không return error - đã xử lý bằng alert
            return Promise.resolve(null);
        }

        // Chỉ reject error cho những trường hợp đặc biệt (401 và 422)
        // Xóa request khỏi manager trước khi reject
        if (originalRequest) {
            requestManager.removeRequest(originalRequest);
        }
        return Promise.reject(error);
    }
);

const MISSING_DATA_RETRY_DESC =
    'Máy chủ tạm thời mất kết nối. Vui lòng tải lại trang hoặc thử lại sau ít phút.';

export function shouldShowMissingDataAlert(res: unknown): boolean {
    if (res == null) return true;
    if (typeof res !== 'object') return true;
    const payload = res as ApiResponse<unknown> & { is401?: boolean; is403?: boolean };
    if (payload.is401 || payload.is403) return false;
    if (payload.success === false) return true;
    if (payload.data == null) return true;
    return false;
}

export function showMissingDataRetryAlert(description = MISSING_DATA_RETRY_DESC): void {
    SweetAlertService.showHandleErrorApiAsync({
        description,
        showRetry: true,
        manualClose: true,
    });
}

/** POST JSON — alert khi success=false hoặc data null (trừ silentError). */
export async function postWithMissingDataAlert<T>(
    url: string,
    body?: unknown,
    config?: InternalAxiosRequestConfig,
): Promise<ApiResponse<T> | null> {
    const res = (await request.post(url, body, config)) as ApiResponse<T> | null;
    if (!config?.silentError && shouldShowMissingDataAlert(res)) {
        showMissingDataRetryAlert();
    }
    return res;
}

// Helper function để clear retry queue (có thể gọi khi cần reset)
export const clearRetryQueue = (): void => {
    retryQueue.clear();
};

// Helper function để check request đang trong retry queue
export const isRequestInRetryQueue = (method: string, url: string): boolean => {
    return retryQueue.has(`${method}-${url}`);
};

/**
 * Header xác thực cho request không đi qua axios (vd. fetch SSE): Bearer (refresh nếu hết hạn),
 * X-Tenant-ID, X-PhanHe. Trả null khi chưa đăng nhập / refresh thất bại.
 */
export const getAuthorizedHeaders = async (): Promise<Record<string, string> | null> => {
    let token = useAuthStore.getState().accessToken;
    if (!token) return null;
    if (tokenService.isTokenExpired(token)) {
        const result = await tokenService.validateAndRefreshToken();
        if (!result.isValid || !result.token) {
            await tokenService.handleTokenExpired();
            return null;
        }
        token = result.token;
    }
    return {
        Authorization: `Bearer ${token}`,
        'X-Tenant-ID': useAuthStore.getState().tenantID ?? '',
        'X-PhanHe': getPhanHeFromStorage() ?? 'bsc',
        'X-Requested-With': 'XMLHttpRequest',
    };
};

/** URL tuyệt đối/tương đối cho path `/v1/...` theo cùng quy tắc baseURL của axios. */
export const resolveApiUrl = (path: string): string => {
    let base = String(request.defaults.baseURL || '');
    const aiHubApiHost = resolveAiHubApiHost();
    if (aiHubApiHost && isAiHubApiPath(path)) base = aiHubApiHost;
    return `${base.replace(/\/$/, '')}${path}`;
};

// Export requestManager để sử dụng ở nơi khác
export { requestManager };

export default request;
